import React, { type FC, type PropsWithChildren, type ReactNode, useMemo } from 'react'
import { Box } from '@mui/material'

import { objectKeys, type ReadonlyNonEmptyArray, type ReadonlyRecord } from '../../../utils'

import type { Translation } from '../../../domain'

import { DetailsHeader } from '../common'
import { useNavigationFormatter } from '../Navigation'
import { type AbsoluteRouteOnlyPath, useHasCapabilities, useRouting } from '../routing'
import { Tab, Tabs } from '.'
import type { TabsProps } from './Tabs'

type RouteTabsProps<R extends string> =
    & Readonly<{
        title: Translation
        tabs: ReadonlyRecord<R, ReadonlyRecord<'documentationTitle' | 'documentationBody', Translation> & Readonly<{ titleActions?: ReactNode }>>
        tabsWrapper?: FC<PropsWithChildren>
    }>
    & Pick<TabsProps<R>, 'orientation'>

const defaultTabsWrapper: FC<PropsWithChildren> = ({ children }) => <>{children}</>

export const RouteTabs = <R extends AbsoluteRouteOnlyPath>({
    title,
    tabs,
    orientation,
    tabsWrapper: TabsWrapper = defaultTabsWrapper,
    ...props
}: RouteTabsProps<R>): ReturnType<FC> => {
    const router = useRouting()
    const hasCapabilities = useHasCapabilities()
    const formatter = useNavigationFormatter()

    const keys = objectKeys(tabs) as readonly R[] as ReadonlyNonEmptyArray<R>

    const currentTab: R = useMemo(() => keys.find((route) => router.isAtRoute(route)) ?? keys[0], [router])

    const { documentationTitle, documentationBody, titleActions } = tabs[currentTab]

    return (
        <Box {...props}>
            <DetailsHeader title={[title, { count: 0 }]} titleActions={titleActions} description={[documentationTitle, documentationBody]} />

            <TabsWrapper>
                <Tabs orientation={orientation} onChange={(value) => router.redirect(value)} value={currentTab}>
                    {keys.map((route) => {
                        const disabled = !hasCapabilities(route)
                        return <Tab key={route} title={formatter.formatTitle(route)} value={route} disabled={disabled} />
                    })}
                </Tabs>
            </TabsWrapper>
        </Box>
    )
}
